/**
 * Lightweight YAML-subset frontmatter parser, replacing the `front-matter` package
 * (which pulled js-yaml + esprima, ~300kb, into the bundle).
 *
 * Supported: nested block mappings, block sequences (including compact `- key: value`
 * items), flow collections (`[a, b]`, `{a: b}`, spanning multiple lines), single- and
 * double-quoted strings with escapes, literal (`|`) and folded (`>`) block scalars with
 * chomping and indentation indicators, comments, and typed plain scalars (booleans,
 * null, integers, floats, infinities).
 *
 * Unsupported constructs throw a descriptive error rather than misparsing silently:
 * anchors, aliases, tags, directives, explicit `?` keys, multi-line plain scalars.
 * Intentional divergences from full YAML: unquoted timestamps stay strings, only
 * true/false spellings are booleans (not yes/no/on/off), and a mapping value may
 * contain ": " (parsed as part of the string).
 */

export type YamlValue = string | number | boolean | null | YamlValue[] | { [key: string]: YamlValue };

export interface FrontmatterResult {
    attributes: Record<string, YamlValue>;
    body: string;
    frontmatter: string;
}

export class FrontmatterParseError extends Error {
    constructor(message: string, lineNum: number) {
        super(`Frontmatter parse error at line ${lineNum}: ${message}`);
        this.name = 'FrontmatterParseError';
    }
}

interface SourceLine {
    raw: string;
    indent: number;
    content: string;
    num: number;
}

interface ParseResult {
    value: YamlValue;
    next: number;
}

export function parseFrontmatter(raw: string): FrontmatterResult {
    const text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
    const origLines = text.split('\n');
    const stripped = origLines.map((l) => (l.endsWith('\r') ? l.slice(0, -1) : l));

    let open = 0;
    while (open < stripped.length && stripped[open]!.trim() === '') open++;
    if (open >= stripped.length || stripped[open]!.trimEnd() !== '---') {
        return { attributes: {}, body: text, frontmatter: '' };
    }

    let close = -1;
    for (let i = open + 1; i < stripped.length; i++) {
        const t = stripped[i]!.trimEnd();
        if (t === '---' || t === '...') {
            close = i;
            break;
        }
    }
    if (close < 0) throw new FrontmatterParseError('unterminated frontmatter: missing closing "---"', open + 1);

    const lines: SourceLine[] = stripped.slice(open + 1, close).map((l, i) => makeLine(l, open + 2 + i));
    return {
        attributes: parseTopLevel(lines),
        body: origLines.slice(close + 1).join('\n'),
        frontmatter: stripped.slice(open + 1, close).join('\n'),
    };
}

function makeLine(raw: string, num: number): SourceLine {
    let indent = 0;
    while (indent < raw.length && raw[indent] === ' ') indent++;
    return { raw, indent, content: raw.slice(indent).trimEnd(), num };
}

function isSkippable(line: SourceLine): boolean {
    return line.content === '' || line.content.startsWith('#');
}

function skipBlank(lines: SourceLine[], i: number): number {
    while (i < lines.length && isSkippable(lines[i]!)) i++;
    return i;
}

function checkTab(line: SourceLine): void {
    if (line.raw[line.indent] === '\t') {
        throw new FrontmatterParseError('tab characters are not allowed in indentation; use spaces', line.num);
    }
}

function isSeqLine(content: string): boolean {
    return content === '-' || content.startsWith('- ');
}

function parseTopLevel(lines: SourceLine[]): Record<string, YamlValue> {
    const first = skipBlank(lines, 0);
    if (first >= lines.length) return {};
    const { value, next } = parseNode(lines, first, 0);
    const rest = skipBlank(lines, next);
    if (rest < lines.length) throw new FrontmatterParseError('unexpected content', lines[rest]!.num);
    if (value === null) return {};
    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new FrontmatterParseError('frontmatter must be a mapping of keys to values', lines[first]!.num);
    }
    return value;
}

function parseNode(lines: SourceLine[], i: number, minIndent: number): ParseResult {
    i = skipBlank(lines, i);
    if (i >= lines.length || lines[i]!.indent < minIndent) return { value: null, next: i };
    const line = lines[i]!;
    checkTab(line);
    if (isSeqLine(line.content)) return parseSequence(lines, i, line.indent);
    return parseMap(lines, i, line.indent);
}

interface FirstLineOverride {
    content: string;
}

function parseMap(lines: SourceLine[], start: number, indent: number, firstOverride?: FirstLineOverride): ParseResult {
    const entries: [string, YamlValue][] = [];
    const seen = new Set<string>();
    let i = start;
    while (i < lines.length) {
        const src = lines[i]!;
        const line = i === start && firstOverride ? { ...src, content: firstOverride.content, indent } : src;
        if (isSkippable(line)) {
            i++;
            continue;
        }
        if (line.indent < indent) break;
        if (line.indent > indent) {
            throw new FrontmatterParseError(
                `bad indentation of a mapping entry (expected ${indent} spaces, got ${line.indent})`,
                line.num,
            );
        }
        checkTab(line);
        if (isSeqLine(line.content)) break;
        if (line.content.startsWith('? ')) {
            throw new FrontmatterParseError('explicit key syntax ("? key") is not supported', line.num);
        }
        const content = stripInlineComment(line.content);
        if ('&*!%'.includes(content[0]!)) {
            throw new FrontmatterParseError(
                'YAML anchors, aliases, tags, and directives are not supported; quote the value if it is a plain string',
                line.num,
            );
        }
        const split = trySplitKey(content, line.num);
        if (!split) {
            throw new FrontmatterParseError(
                'expected a "key: value" mapping entry (multi-line plain scalars are not supported; use a block scalar or quotes)',
                line.num,
            );
        }
        if (seen.has(split.key)) throw new FrontmatterParseError(`duplicate key "${split.key}"`, line.num);
        seen.add(split.key);
        const parsed = parseValue(split.rest, lines, i, line.indent, true, line.num);
        entries.push([split.key, parsed.value]);
        i = parsed.next;
    }
    return { value: Object.fromEntries(entries), next: i };
}

function parseSequence(
    lines: SourceLine[],
    start: number,
    indent: number,
    firstOverride?: FirstLineOverride,
): ParseResult {
    const items: YamlValue[] = [];
    let i = start;
    while (i < lines.length) {
        const src = lines[i]!;
        const line = i === start && firstOverride ? { ...src, content: firstOverride.content, indent } : src;
        if (isSkippable(line)) {
            i++;
            continue;
        }
        if (line.indent < indent) break;
        if (!isSeqLine(line.content)) {
            if (line.indent === indent) break;
            throw new FrontmatterParseError('bad indentation of a sequence entry', line.num);
        }
        if (line.indent > indent) {
            throw new FrontmatterParseError('bad indentation of a sequence entry', line.num);
        }
        checkTab(line);
        const content = stripInlineComment(line.content);
        let k = 1;
        while (k < content.length && content[k] === ' ') k++;
        const rest = content.slice(k);
        const itemCol = line.indent + k;
        let parsed: ParseResult;
        if (rest === '') {
            parsed = parseValue('', lines, i, line.indent, false, line.num);
        } else if (isSeqLine(rest)) {
            parsed = parseSequence(lines, i, itemCol, { content: rest });
        } else if (trySplitKey(rest, line.num)) {
            parsed = parseMap(lines, i, itemCol, { content: rest });
        } else {
            parsed = parseValue(rest, lines, i, line.indent, false, line.num);
        }
        items.push(parsed.value);
        i = parsed.next;
    }
    return { value: items, next: i };
}

const BLOCK_SCALAR_HEADER_RE = /^([|>])([0-9+-]*)$/;

function parseValue(
    rest: string,
    lines: SourceLine[],
    i: number,
    ownerIndent: number,
    isMapValue: boolean,
    lineNum: number,
): ParseResult {
    if (rest === '') {
        const j = skipBlank(lines, i + 1);
        if (j < lines.length) {
            const ln = lines[j]!;
            if (ln.indent > ownerIndent) return parseNode(lines, j, ownerIndent + 1);
            if (isMapValue && ln.indent === ownerIndent && isSeqLine(ln.content)) {
                return parseSequence(lines, j, ln.indent);
            }
        }
        return { value: null, next: i + 1 };
    }

    const first = rest[0]!;

    if (first === '|' || first === '>') {
        const m = BLOCK_SCALAR_HEADER_RE.exec(rest);
        if (!m) throw new FrontmatterParseError(`invalid block scalar header "${rest}"`, lineNum);
        let chomp: '' | '-' | '+' = '';
        let explicit: number | undefined;
        for (const c of m[2]!) {
            if (c === '+' || c === '-') {
                if (chomp) throw new FrontmatterParseError('duplicate chomping indicator', lineNum);
                chomp = c;
            } else {
                if (explicit !== undefined) throw new FrontmatterParseError('duplicate indentation indicator', lineNum);
                if (c === '0') {
                    throw new FrontmatterParseError('block scalar indentation indicator must be 1-9', lineNum);
                }
                explicit = Number(c);
            }
        }
        return readBlockScalar(lines, i + 1, ownerIndent, m[1] as '|' | '>', chomp, explicit);
    }

    if ('&*!%'.includes(first)) {
        throw new FrontmatterParseError(
            'YAML anchors, aliases, tags, and directives are not supported; quote the value if it is a plain string',
            lineNum,
        );
    }

    if (first === '[' || first === '{') {
        let text = rest;
        let j = i;
        while (!isFlowComplete(text)) {
            j++;
            if (j >= lines.length) throw new FrontmatterParseError('unterminated flow collection', lineNum);
            const ln = lines[j]!;
            if (isSkippable(ln)) continue;
            text += ' ' + stripInlineComment(ln.content);
        }
        return { value: parseFlow(text, lineNum), next: j + 1 };
    }

    if (first === '"' || first === "'") {
        const q = readQuoted(rest, 0, lineNum);
        if (rest.slice(q.end).trim() !== '') {
            throw new FrontmatterParseError('unexpected content after quoted scalar', lineNum);
        }
        return { value: q.value, next: i + 1 };
    }

    if (rest.startsWith('- ')) {
        throw new FrontmatterParseError('a block sequence must start on its own line', lineNum);
    }

    return { value: parsePlainScalar(rest), next: i + 1 };
}

interface KeySplit {
    key: string;
    rest: string;
}

function trySplitKey(content: string, lineNum: number): KeySplit | null {
    if (content === '' || content[0] === '[' || content[0] === '{') return null;
    if (content[0] === '"' || content[0] === "'") {
        let q: { value: string; end: number };
        try {
            q = readQuoted(content, 0, lineNum);
        } catch {
            return null;
        }
        let j = q.end;
        while (content[j] === ' ') j++;
        if (content[j] !== ':') return null;
        if (j + 1 < content.length && content[j + 1] !== ' ') return null;
        return { key: q.value, rest: content.slice(j + 1).trim() };
    }
    for (let j = 0; j < content.length; j++) {
        if (content[j] === ':' && (j + 1 === content.length || content[j + 1] === ' ')) {
            const key = content.slice(0, j).trimEnd();
            if (key === '') return null;
            return { key, rest: content.slice(j + 1).trim() };
        }
    }
    return null;
}

/** Strip a trailing comment: `#` at line start or preceded by whitespace, outside quotes. */
function stripInlineComment(s: string): string {
    let inSingle = false;
    let inDouble = false;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (inDouble) {
            if (ch === '\\') i++;
            else if (ch === '"') inDouble = false;
        } else if (inSingle) {
            if (ch === "'") {
                if (s[i + 1] === "'") i++;
                else inSingle = false;
            }
        } else if (ch === '"') {
            inDouble = true;
        } else if (ch === "'") {
            inSingle = true;
        } else if (ch === '#' && (i === 0 || s[i - 1] === ' ' || s[i - 1] === '\t')) {
            return s.slice(0, i).trimEnd();
        }
    }
    return s;
}

function readBlockScalar(
    lines: SourceLine[],
    start: number,
    keyIndent: number,
    style: '|' | '>',
    chomp: '' | '-' | '+',
    explicit: number | undefined,
): ParseResult {
    let blockIndent = explicit !== undefined ? keyIndent + explicit : -1;
    const collected: string[] = [];
    let i = start;
    while (i < lines.length) {
        const line = lines[i]!;
        if (line.raw.trim() === '') {
            collected.push('');
            i++;
            continue;
        }
        if (blockIndent < 0) {
            if (line.indent <= keyIndent) break;
            blockIndent = line.indent;
        }
        if (line.indent < blockIndent) break;
        collected.push(line.raw.slice(blockIndent));
        i++;
    }

    let trailing = 0;
    while (collected.length > 0 && collected[collected.length - 1] === '') {
        collected.pop();
        trailing++;
    }

    let value: string;
    if (collected.length === 0) {
        value = chomp === '+' ? '\n'.repeat(trailing) : '';
    } else {
        const body = style === '|' ? collected.join('\n') : foldLines(collected);
        if (chomp === '-') value = body;
        else if (chomp === '+') value = body + '\n'.repeat(trailing + 1);
        else value = body + '\n';
    }
    return { value, next: i };
}

/** Folded (`>`) scalar joining: single breaks between text lines become spaces,
 * blank lines become newlines, breaks adjacent to more-indented lines are kept. */
function foldLines(ls: string[]): string {
    let out = '';
    let last: 'none' | 'text' | 'indented' = 'none';
    let blanks = 0;
    for (const l of ls) {
        if (l === '') {
            blanks++;
            continue;
        }
        const indented = l[0] === ' ' || l[0] === '\t';
        if (last === 'none') {
            out += '\n'.repeat(blanks);
        } else {
            const foldable = last === 'text' && !indented;
            if (blanks === 0) out += foldable ? ' ' : '\n';
            else out += '\n'.repeat(blanks + (foldable ? 0 : 1));
        }
        out += l;
        last = indented ? 'indented' : 'text';
        blanks = 0;
    }
    return out;
}

const DOUBLE_QUOTE_ESCAPES: Record<string, string> = {
    '0': '\0',
    a: '\x07',
    b: '\b',
    t: '\t',
    n: '\n',
    v: '\v',
    f: '\f',
    r: '\r',
    e: '\x1b',
    ' ': ' ',
    '"': '"',
    '/': '/',
    '\\': '\\',
    N: '\u0085',
    _: '\u00a0',
    L: '\u2028',
    P: '\u2029',
};

function readQuoted(s: string, pos: number, lineNum: number): { value: string; end: number } {
    const quote = s[pos]!;
    let out = '';
    let i = pos + 1;
    if (quote === "'") {
        while (i < s.length) {
            if (s[i] === "'") {
                if (s[i + 1] === "'") {
                    out += "'";
                    i += 2;
                    continue;
                }
                return { value: out, end: i + 1 };
            }
            out += s[i];
            i++;
        }
        throw new FrontmatterParseError(
            'unterminated single-quoted string (quoted strings must close on the same line)',
            lineNum,
        );
    }
    while (i < s.length) {
        const ch = s[i]!;
        if (ch === '"') return { value: out, end: i + 1 };
        if (ch === '\\') {
            const esc = s[i + 1];
            if (esc === undefined) {
                throw new FrontmatterParseError('unterminated escape sequence in double-quoted string', lineNum);
            }
            if (esc === 'x' || esc === 'u' || esc === 'U') {
                const len = esc === 'x' ? 2 : esc === 'u' ? 4 : 8;
                const hex = s.slice(i + 2, i + 2 + len);
                if (hex.length !== len || !/^[0-9a-fA-F]+$/.test(hex)) {
                    throw new FrontmatterParseError(`invalid \\${esc} escape sequence`, lineNum);
                }
                out += String.fromCodePoint(parseInt(hex, 16));
                i += 2 + len;
                continue;
            }
            const mapped = DOUBLE_QUOTE_ESCAPES[esc];
            if (mapped === undefined) {
                throw new FrontmatterParseError(`unsupported escape "\\${esc}" in double-quoted string`, lineNum);
            }
            out += mapped;
            i += 2;
            continue;
        }
        out += ch;
        i++;
    }
    throw new FrontmatterParseError(
        'unterminated double-quoted string (quoted strings must close on the same line)',
        lineNum,
    );
}

function isFlowComplete(text: string): boolean {
    let depth = 0;
    let inSingle = false;
    let inDouble = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inDouble) {
            if (ch === '\\') i++;
            else if (ch === '"') inDouble = false;
        } else if (inSingle) {
            if (ch === "'") {
                if (text[i + 1] === "'") i++;
                else inSingle = false;
            }
        } else if (ch === '"') {
            inDouble = true;
        } else if (ch === "'") {
            inSingle = true;
        } else if (ch === '[' || ch === '{') {
            depth++;
        } else if (ch === ']' || ch === '}') {
            depth--;
        }
    }
    return depth <= 0 && !inSingle && !inDouble;
}

interface FlowParser {
    s: string;
    i: number;
    lineNum: number;
}

function parseFlow(text: string, lineNum: number): YamlValue {
    const p: FlowParser = { s: text, i: 0, lineNum };
    const value = flowValue(p, false);
    skipFlowWs(p);
    if (p.i < p.s.length) {
        throw new FrontmatterParseError('unexpected content after flow collection', lineNum);
    }
    return value;
}

function skipFlowWs(p: FlowParser): void {
    while (p.s[p.i] === ' ' || p.s[p.i] === '\t') p.i++;
}

function flowValue(p: FlowParser, insideSequence: boolean): YamlValue {
    skipFlowWs(p);
    const ch = p.s[p.i];
    if (ch === undefined) throw new FrontmatterParseError('unexpected end of flow collection', p.lineNum);
    if (ch === '[') return flowSequence(p);
    if (ch === '{') return flowMapping(p);
    if (ch === '"' || ch === "'") {
        const q = readQuoted(p.s, p.i, p.lineNum);
        p.i = q.end;
        return q.value;
    }
    if ('&*!%'.includes(ch)) {
        throw new FrontmatterParseError(
            'YAML anchors, aliases, tags, and directives are not supported in flow collections; quote the value',
            p.lineNum,
        );
    }
    let j = p.i;
    while (j < p.s.length && !',]}'.includes(p.s[j]!)) j++;
    const token = p.s.slice(p.i, j).trim();
    if (token === '') throw new FrontmatterParseError('empty entry in flow collection', p.lineNum);
    if (insideSequence && (token.includes(': ') || token.endsWith(':'))) {
        throw new FrontmatterParseError(
            'single-pair mappings inside flow sequences are not supported; use an explicit {key: value} or quote the value',
            p.lineNum,
        );
    }
    p.i = j;
    return parsePlainScalar(token);
}

function flowSequence(p: FlowParser): YamlValue[] {
    p.i++;
    const items: YamlValue[] = [];
    skipFlowWs(p);
    if (p.s[p.i] === ']') {
        p.i++;
        return items;
    }
    for (;;) {
        items.push(flowValue(p, true));
        skipFlowWs(p);
        const ch = p.s[p.i];
        if (ch === ',') {
            p.i++;
            skipFlowWs(p);
            if (p.s[p.i] === ']') {
                p.i++;
                return items;
            }
            continue;
        }
        if (ch === ']') {
            p.i++;
            return items;
        }
        throw new FrontmatterParseError('expected "," or "]" in flow sequence', p.lineNum);
    }
}

function flowMapping(p: FlowParser): Record<string, YamlValue> {
    p.i++;
    const entries: [string, YamlValue][] = [];
    const seen = new Set<string>();
    skipFlowWs(p);
    if (p.s[p.i] === '}') {
        p.i++;
        return {};
    }
    for (;;) {
        skipFlowWs(p);
        const ch = p.s[p.i];
        if (ch === undefined) throw new FrontmatterParseError('unterminated flow mapping', p.lineNum);
        let key: string;
        if (ch === '"' || ch === "'") {
            const q = readQuoted(p.s, p.i, p.lineNum);
            key = q.value;
            p.i = q.end;
        } else {
            let j = p.i;
            while (j < p.s.length && !':,}'.includes(p.s[j]!)) j++;
            key = p.s.slice(p.i, j).trim();
            p.i = j;
        }
        if (key === '') throw new FrontmatterParseError('empty key in flow mapping', p.lineNum);
        skipFlowWs(p);
        let value: YamlValue = null;
        if (p.s[p.i] === ':') {
            p.i++;
            value = flowValue(p, false);
        }
        if (seen.has(key)) throw new FrontmatterParseError(`duplicate key "${key}"`, p.lineNum);
        seen.add(key);
        entries.push([key, value]);
        skipFlowWs(p);
        const sep = p.s[p.i];
        if (sep === ',') {
            p.i++;
            skipFlowWs(p);
            if (p.s[p.i] === '}') {
                p.i++;
                break;
            }
            continue;
        }
        if (sep === '}') {
            p.i++;
            break;
        }
        throw new FrontmatterParseError('expected "," or "}" in flow mapping', p.lineNum);
    }
    return Object.fromEntries(entries);
}

const INT_RE = /^[-+]?\d+$/;
const HEX_RE = /^0x[0-9a-fA-F]+$/;
const OCTAL_RE = /^0o[0-7]+$/;
const FLOAT_RE = /^[-+]?(\d+\.\d*|\.\d+|\d+)([eE][-+]?\d+)?$/;
const INF_RE = /^[-+]?\.(inf|Inf|INF)$/;
const NAN_RE = /^\.(nan|NaN|NAN)$/;

function parsePlainScalar(token: string): YamlValue {
    switch (token) {
        case 'null':
        case 'Null':
        case 'NULL':
        case '~':
            return null;
        case 'true':
        case 'True':
        case 'TRUE':
            return true;
        case 'false':
        case 'False':
        case 'FALSE':
            return false;
    }
    if (INT_RE.test(token)) return parseInt(token, 10);
    if (HEX_RE.test(token)) return parseInt(token.slice(2), 16);
    if (OCTAL_RE.test(token)) return parseInt(token.slice(2), 8);
    if (FLOAT_RE.test(token) && /[.eE]/.test(token)) return parseFloat(token);
    if (INF_RE.test(token)) return token[0] === '-' ? -Infinity : Infinity;
    if (NAN_RE.test(token)) return NaN;
    return token;
}
